import { Component, Input, OnInit, OnChanges, SimpleChanges,
    ViewChildren, ViewContainerRef, QueryList } from '@angular/core';
import { FileUpload, FileUploadEvent } from 'primeng/fileupload';

import { DynamicComponent, GenericValue, HisUiService, HisAuthService, HisNotificationService } from 'hisng';

import { DYNAMIC_FIELD_ANCHOR } from 'app/fs/res/common/model/dynamic-content/constants';
import { DocumentHelperService } from 'app/fs/res/common/service/documents/document-helper.service';
import { DocumentMetadataCategoryService } from 'app/fs/res/common/service/documents/documentMetadataCategory.service';
import { DocumentService } from 'app/fs/res/common/service/documents/document.service';
import { ProviderManager } from 'app/fs/res/common/utils/provider-manager';
import { DocumentMetadata, Mimedata } from 'app/fs/res/common/model/documents/documentMetadata';

@Component( {
    selector: 'res-documents-edit',
    templateUrl: './documents-edit.component.html',
    styleUrls: ['./documents-edit.component.css'],
    providers: [ProviderManager.getComponentProvider(DocumentsEditComponent)],
} )
export class DocumentsEditComponent implements OnInit, OnChanges {
    @Input() target: string;
    @Input() targetId: number;
    @Input() targetObjGuid: string;
    @Input() hisDdKey = 'fs.res.common.documents'; // for custom header label
    @Input() acceptedFileTypes: string; // allowed file types
    @Input() documentsToSave: DocumentMetadata[] = []; // emit changed or new documents
    @Input() documentsRights = true;
    @Input() dynamicComponent: DynamicComponent;
    @ViewChildren( DYNAMIC_FIELD_ANCHOR, { read: ViewContainerRef } ) fieldViewContainerList: QueryList<ViewContainerRef>;
    @ViewChildren( 'fileUploadComponent' ) fileUploadComponents: QueryList<FileUpload>;

    public documents: DocumentMetadata[] = []; // documents for view
    public dateAndTimeFormat: string;
    public uploadUrl: string; // p-fileUpload Component
    public fileUploadId = 'fileUpload_'; // static part of <p-fileUpload>-Component Id
    public documentCategoryOptions: GenericValue[];

    private readonly MAX_DOC_FILE_SIZE = 50000000; // 50 MB (50000000 Bytes) max file size for text files
    private readonly MAX_IMAGE_FILE_SIZE = 2000000; // 2 MB (2000000 Bytes) max file size for image files
    private readonly BYTES_TO_MB = 1000000; // divisor bytes to mb

    constructor( public auth: HisAuthService,
        private hisUiService: HisUiService,
        private notificationService: HisNotificationService,
        private documentService: DocumentService,
        private documentMetadataCategoryService: DocumentMetadataCategoryService,
        public documentHelperService: DocumentHelperService ) {
    }

    /* Component ngOnInit */
    ngOnInit() {
        // get dateFormat and timeFormatShort
        this.dateAndTimeFormat = this.hisUiService.translate( 'common.dateFormat' )
            + ' ' + this.hisUiService.translate( 'common.timeFormatShort' );
    }

    /* Component ngOnChanges  */
    ngOnChanges( changes: SimpleChanges ) {
        if ( !this.documentsToSave.length && this.targetObjGuid ) {
            // get all documents for target with given id
            this.documentService.getDocumentMetadataByTargetNameAndObjGuid( this.target, this.targetObjGuid ).subscribe(
                ( documentMetadata: DocumentMetadata[] ) => {
                    console.log( 'loaded documentMetadata: ', documentMetadata );
                    this.documents = documentMetadata;
                } );
            // set uploadUrl for changed target and targetId
            // build upload URL
            let url = window.location.href;
            this.uploadUrl = url.substring( 0, url.indexOf( '/a/' ) ) + '/api/v1/cs/sys/filemanagement/uploadFile/';
            this.uploadUrl = this.uploadUrl + `${ this.target }/${ this.targetId }`;
            console.log( 'uploadUrl =', this.uploadUrl );
        }
    }

    /**
     * add new documentMeta object to array
     */
    addDocument() {
        let documentMetadata: DocumentMetadata = new DocumentMetadata();
        documentMetadata.target = this.target;
        documentMetadata.targetId = this.targetId;
        documentMetadata.mimedata = new Mimedata();
        if ( !this.documents ) {
            this.documents = [];
        }
        this.documents = [ ...this.documents, documentMetadata ];
        this.setDocumentMetadataToSave( this.documents.indexOf( documentMetadata ) );
    }

    /* documentMetadata.mimedata.description changed */
    descriptionChanged( event: string, index: number ): void {
        this.documents[index].mimedata.description = event.trim();
        // set and emit for changed documentMetadata
        this.setDocumentMetadataToSave( index );
    }

    /** set documentMetadata.category onSelect */
    public onSelectDocumentCategory( event: GenericValue, index: number ): void {
        this.documents[index].category = event;
        // set and emit for changed documentMetadata
        this.setDocumentMetadataToSave( index );
    }

    /** get/load document categories */
    public documentCategoryTypes( event: { query: string } ) {
        this.documentMetadataCategoryService.getDocumentMetadataCategories( this.target ).subscribe( (dC: GenericValue[]) => {
            this.documentCategoryOptions = dC.filter( d =>
                d.defaulttext.toLowerCase().includes( event.query.toLowerCase() )
            );
        } );
    }

    /**
     * set changed documentMetadata for save service
     *
     * @param indexOfOrginal of original changed documentMetadata object
     */
    private setDocumentMetadataToSave( indexOfOrginal: number ): void {
        let index = this.documentsToSave.indexOf( this.documents[indexOfOrginal] );
        // if id of document is not null
        if ( index < 0 ) {
            this.documentsToSave.push( this.documents[indexOfOrginal] );
        } else {
            this.documentsToSave[index] = this.documents[indexOfOrginal];
        }
    }

    /* set selected file and provide auto information */
    onSelectFile( event: { files: File[] }, index: number, documentMetadata: DocumentMetadata ) {
        console.log( 'event.files[0].name:', event.files[0].name );
        let selectedFile: File = event.files[0];
        documentMetadata.fileName = null; // reset fileName

        this.fileUploadComponents.forEach( f => {
            if ( this.getFileUploadComponentParentNodeId( f ) === ( this.fileUploadId + index ) ) {
                f.files = []; // reset fileUploadComponents.files
                f.files.push( selectedFile ); // set new file for fileUploadComponents.files
                // set maxFileSize for component when file type is known
                this.setFileSize( index );
                f.msgs = null;
                // check file size and file type are valid
                if ( this.validateFileType( selectedFile ) && this.validateFileSize( selectedFile ) ) {

                    // check for mime content type via backend service
                    this.documentService.mimeContentType( selectedFile ).subscribe( (mimeContentType: string) => {
                        if ( mimeContentType === selectedFile.type ) {
                            documentMetadata.fileName = selectedFile.name;
                            documentMetadata.mimedata.mimeType = selectedFile.type;
                        } else {
                            documentMetadata.fileName = null; // reset fileName
                            this.notificationService.error(
                                this.hisUiService.translate(
                                    'de.his.javang.gefunden.pictureFileNameInconsistentError',
                                    selectedFile.name,
                                    mimeContentType ),
                                this.hisUiService.translate( 'view.messages.error' )
                            );
                        }
                    } );
                }
            }
        } );
    }

    /* validate file against allowed/accepted file types */
    private validateFileType( selectedFile: File ): boolean {
        // if file type not supported
        if ( !this.acceptedFileTypes.includes( selectedFile.name.split( '.' ).pop().toLowerCase() ) ) {
            this.notificationService.error(
                this.hisUiService.translate(
                    'fs.res.common.documents.invalidFileType',
                    selectedFile.name,
                    this.acceptedFileTypes ),
                this.hisUiService.translate( 'view.messages.error' )
            );
            return false;
        }
        return true;
    }

    /* validate file size against allowed max file size depending document type */
    private validateFileSize( selectedFile: File ): boolean {
        let allowedFileSize = selectedFile.type.includes( 'image' ) ? this.MAX_IMAGE_FILE_SIZE : this.MAX_DOC_FILE_SIZE;
        if ( selectedFile.size > allowedFileSize ) {
            this.notificationService.error(
                this.hisUiService.translate(
                    'fs.res.common.documents.invalidFileSize',
                    selectedFile.name,
                    ( this.MAX_DOC_FILE_SIZE / this.BYTES_TO_MB ),
                    ( this.MAX_IMAGE_FILE_SIZE / this.BYTES_TO_MB )
                ),
                this.hisUiService.translate( 'view.messages.error' )
            );
            return false;
        }
        return true;
    }

    /* implementation of upload-Button */
    upload( index: number, documentMetadata: DocumentMetadata ) {
        documentMetadata.mimedata.createdAt = new Date(); // set date already to identify object
        this.fileUploadComponents.forEach( f => {
            if ( this.getFileUploadComponentParentNodeId( f ) === ( this.fileUploadId + index ) ) {
                // via method of FileUploadComponet.upload();
                f.upload(); // upload file for component with correct Id
            }
        } );

        // emit changes for documents, so they could be saved
        this.setDocumentMetadataToSave( this.documents.indexOf( documentMetadata ) );

        // changes detected - save required info
        this.showChangeSaveInfoMessage();
    }

    onUpload( event: FileUploadEvent, index: number ) {
        // The only way to get the result from p-fileupload is via the response;
        // that's a string that we still need to convert to object
        let response: DocumentMetadata = JSON.parse( (event as any).xhr.response );
        this.documents[index].mimedata.mimeType = response.mimedata.mimeType;
        this.setDocumentMetadataToSave( index );
    }


    //    /* get file component id by native element navigation through parent node */
    getFileUploadComponentParentNodeId( fileUploadComponent: FileUpload ) {
        // cause with current PrimeNG Version 5.2.0 el: ElementRef of FileUpload is private we can not access id directly
        // console.log( 'found <p-fileUpload>-Component with Id = ',
        // fileUploadComponent.content.nativeElement.parentNode.parentNode.id );
        return fileUploadComponent.content.nativeElement.parentNode.parentNode.id;
    }

    /**
     * remove documentMetadata array
     *
     * @param document (documentMetadata to delete)
     */
    removeDocument( document: DocumentMetadata ) {
        const index = this.documents.indexOf( document );
        // if this.documents[index].id == null, new unsaved document
        if ( this.documents[index].id == null ) {
            let toSaveIndex = this.documentsToSave.indexOf( this.documents[index] );
            this.documentsToSave.splice( toSaveIndex, 1 );
        } else {
            this.documents[index].markForDelete = true; // set createdAt to null to mark for delete
            this.setDocumentMetadataToSave( index );

            // changes detected - save required info
            this.showChangeSaveInfoMessage();
        }
        this.documents = this.documents.filter( doc => doc !== document );
    }

    /**
     * set file size for FileUpload component
     *
     * @param index to determine <p-fileUpload>-Component
     */
    setFileSize( index: number ) {
        this.fileUploadComponents.forEach( f => {
            if ( this.getFileUploadComponentParentNodeId( f ) === ( this.fileUploadId + index ) ) {
                if ( this.documents[index].mimedata.mimeType ) {
                    if ( this.documents[index].mimedata.mimeType.includes( 'image' ) ) {
                        f.maxFileSize = this.MAX_IMAGE_FILE_SIZE;
                    } else {
                        f.maxFileSize = this.MAX_DOC_FILE_SIZE;
                    }
                }
            }
            console.log( 'f.maxFileSize: ', f.maxFileSize );
        } );
    }

    /* get file size of selected file from <p-fileUpload>-Component */
    getFileSize( index: number ): string {
        let fileSize = '0';
        this.fileUploadComponents.forEach( f => {
            if ( this.getFileUploadComponentParentNodeId( f ) === ( this.fileUploadId + index ) ) {
                fileSize = '' + f.files[0].size; // set file size
            }
        } );
        return fileSize;
    }

    /* show changes save info message */
    showChangeSaveInfoMessage(): void {
        this.notificationService.info(
            this.hisUiService.translate( 'de.his.javang.gefunden.change' ),
            this.hisUiService.translate( 'view.messages.information' )
        );
    }

    /* get max file size message with parameters */
    getMaxFileSizeMessage(): string {
        return this.hisUiService.translate(
            'fs.res.common.documents.DocumentUploadField.maxSize',
            ( this.MAX_DOC_FILE_SIZE / this.BYTES_TO_MB ) + ' MB',
            ( this.MAX_IMAGE_FILE_SIZE / this.BYTES_TO_MB ) + ' MB' );
    }

    /* show save info message if targetId is null */
    getNoTargetIdSaveInfoMessage() {
        return this.hisUiService.translate(
            'fs.res.common.documents.noTargetIdSaveInfo' );
    }
}
